import { useEffect, useState } from 'react';
import { useApiClient } from '../lib/apiClient';
import { useI18n } from '../i18n/LanguageContext';
import {
  formatCurrency,
  formatDateTime,
  resolveFormattingLocale,
  type UiLanguage,
} from '../i18n/locale';

type OrgFormattingState = { country: string | null; defaultCurrency: string };

// Session-scoped cache: many components share one /users/me read for the org's
// country + default currency (mirrors useClinicType).
let cache: OrgFormattingState | null = null;
let inflight: Promise<OrgFormattingState> | null = null;

type FetchJson = ReturnType<typeof useApiClient>['fetchJson'];

function loadOrgFormatting(fetchJson: FetchJson): Promise<OrgFormattingState> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetchJson<{ organization?: { country?: string | null; defaultCurrency?: string | null } }>('/users/me')
      .then((payload) => {
        cache = {
          country: payload?.organization?.country ?? null,
          defaultCurrency: payload?.organization?.defaultCurrency || 'USD',
        };
        return cache;
      })
      .catch(() => {
        inflight = null;
        return { country: null, defaultCurrency: 'USD' } as OrgFormattingState;
      });
  }
  return inflight;
}

/**
 * Locale-aware formatting bound to the org's country + default currency and the
 * active UI language. Use `formatMoney`/`formatDate` instead of hardcoding `USD`
 * or `undefined` locales so Mexican (and other) clinics format natively.
 */
export function useOrgFormatting() {
  const { fetchJson } = useApiClient();
  const { effectiveLanguage } = useI18n();
  const [state, setState] = useState<OrgFormattingState | null>(cache);

  useEffect(() => {
    if (cache) {
      setState(cache);
      return;
    }
    let active = true;
    void loadOrgFormatting(fetchJson).then((next) => {
      if (active) setState(next);
    });
    return () => {
      active = false;
    };
  }, [fetchJson]);

  const country = state?.country ?? null;
  const currency = state?.defaultCurrency ?? 'USD';
  const locale = resolveFormattingLocale(effectiveLanguage as UiLanguage, country);

  return {
    locale,
    currency,
    country,
    loading: state === null,
    formatMoney: (value: number | null | undefined, currencyOverride?: string) =>
      formatCurrency(value, { currency: currencyOverride || currency, locale }),
    formatDate: (value: string | number | Date | null | undefined, opts: Intl.DateTimeFormatOptions = {}) =>
      formatDateTime(value, { locale, ...opts }),
  };
}
